// ─── External libraries ────────────────────────────────────────────────────
import { useAuth0 } from "@auth0/auth0-react";
import { showToast } from "@papercup-ai/orizuru";
import * as Sentry from "@sentry/nextjs";
import mixpanel from "mixpanel-browser";
import * as React from "react";
import { useContext, useEffect, useState } from "react";

// ─── Internal imports ──────────────────────────────────────────────────────
import { Auth0User, Auth0UserMetadata, Profile } from "../../@types/data";
import { DEV } from "../../constants/env";
import { ProfileContext } from "../../context";
import { parseAuth0Profile, useAccessToken } from "./auth0Utils";
import { AUTH_REQUESTED_KEY, AUTH_TYPE_KEY } from "./utils";

export const useProfile = () => useContext(ProfileContext);

// ApolloProvider's one-shot MY_PROFILE query is the only place that sets
// binderUserId, and it does not re-run when a profile reset happens for the
// same user (e.g. a new Auth0 `user` reference from a token exchange
// completing). Carry it forward in that case so it isn't lost for the rest
// of the session.
const carryOverBinderUserId = (
  prevProfile: Profile | null | undefined,
  nextProfile: Profile
): Profile => {
  if (prevProfile?.sub === nextProfile.sub && prevProfile?.binderUserId) {
    return { ...nextProfile, binderUserId: prevProfile.binderUserId };
  }
  return nextProfile;
};

// Default shape for user_metadata — ensures every field is always defined
// even when Auth0 returns a partial or empty object.
const EMPTY_USER_METADATA: Auth0UserMetadata = {
  contactNumber: undefined,
  contentTypes: undefined,
  country: undefined,
  sourceLanguage: undefined,
  targetLanguage: undefined,
  preferredName: undefined,
  timezone: undefined,
  workTimePreference: undefined
};

const ProfileProvider = ({ children }: { children: React.ReactNode }) => {
  // ─── Auth0 ───────────────────────────────────────────────────────────────
  const {
    isAuthenticated,
    isLoading: isAuth0UserLoading,
    user,
    logout
  } = useAuth0();

  // ─── State ───────────────────────────────────────────────────────────────
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userData, setUserData] = useState<Auth0User>();
  const [authProvider, setAuthProvider] = useState<"auth0">("auth0");

  // Two-phase loading guard:
  //   1. isAuth0UserLoading       — Auth0 is resolving the user from its servers.
  //   2. isAuth0BinderTokenLoading — waiting for the Binder API access token.
  // Both must resolve before the app considers authentication complete.
  const [isAuth0Loading, setIsAuth0Loading] = useState(true);
  const [isAuth0BinderTokenLoading, setIsAuth0BinderTokenLoading] =
    useState<boolean>(true);

  // Prevents reading browser-only APIs (sessionStorage, window) during SSR.
  const [isMounted, setIsMounted] = useState(false);

  // ─── Refs ────────────────────────────────────────────────────────────────
  const isMixpanelInitialisedRef = React.useRef(false);

  // Caches user_metadata fetched from the Management API so it is available
  // regardless of which arrives first: the API response or the auth useEffect.
  // This resolves a deep-link race condition where onToken (triggered by a fast
  // localStorage cache hit) fires before isMounted is true, causing userData to
  // still be undefined when the Management API response arrives.
  const apiUserMetadataRef = React.useRef<Auth0UserMetadata | null>(null);

  // ─── Derived state ───────────────────────────────────────────────────────

  const hasRole = React.useMemo(
    () => Object?.values(profile?.groups || {})?.includes(true),
    [profile]
  );

  // ─── Effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Initialise Mixpanel once the profile (and therefore user sub) is known.
  useEffect(() => {
    if (profile?.sub && process.env.NEXT_PUBLIC_MIXPANEL_ID) {
      mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_ID, {
        debug: DEV,
        verbose: true,
        // [rws] api_host usage:
        //  - geographic data residency: https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#eu-data-residency
        //  - tracking via proxy (reduces ad-blockers impact on tracking): https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#tracking-via-proxy
        // served by mix-panel-proxy from charts repo - charts/mixpanel-proxy/values.yaml
        // [rws] temporarily commented out to prevent calls to mp.papercup.com; api_host can be undefined according to typing
        //api_host: "https://mp.papercup.com",
        // @ts-ignore
        ignore_dnt: true
      });
      isMixpanelInitialisedRef.current = true;
      mixpanel.identify(profile.sub);
      mixpanel.people.set(profile);
      mixpanel.track("sign in");
    }
  }, [profile]);

  // Sets up profile and base userData once Auth0 resolves the authenticated user.
  // Note: user_metadata is NOT present in the Auth0 ID token — the Post Login
  // Action does not inject it. It is loaded separately by fetchUserMetadataOnLoad
  // below. If the Management API response has already arrived (fast cache hit),
  // apiUserMetadataRef.current will be populated and used immediately here;
  // otherwise it defaults to empty metadata until the API response arrives.
  useEffect(() => {
    if (!isMounted || !isAuthenticated || !user) return;

    setAuthProvider("auth0");
    sessionStorage.setItem(AUTH_TYPE_KEY, "auth0");
    setProfile(prevProfile =>
      carryOverBinderUserId(prevProfile, parseAuth0Profile(user))
    );
    setUserData({
      ...(user as unknown as Auth0User),
      user_metadata: apiUserMetadataRef.current ?? { ...EMPTY_USER_METADATA }
    });

    Sentry.setUser({ email: user?.email, username: user?.name });
  }, [isMounted, isAuth0UserLoading, isAuthenticated, user]);

  // Drives isAuth0Loading: app is ready only when both the user and Binder
  // token have resolved.
  useEffect(() => {
    if (isAuth0UserLoading) {
      setIsAuth0Loading(true);
      return;
    }
    setIsAuth0Loading(isAuthenticated ? isAuth0BinderTokenLoading : false);
  }, [isAuth0UserLoading, isAuth0BinderTokenLoading, isAuthenticated]);

  // ─── Token acquisition ────────────────────────────────────────────────────

  // Binder token — used for all GraphQL / API calls throughout the app.
  const [binderTokenRef, refreshTokenCallback] = useAccessToken(
    {
      shouldRefreshToken: Boolean(user?.sub),
      auth0Options: {
        audience: process.env.NEXT_PUBLIC_AUTH0_PAPERCUP_SERVICES_AUDIENCE || ""
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onToken: () => {
        setIsAuth0BinderTokenLoading(false);
      }
    },
    [isAuthenticated]
  );

  // Fetches user_metadata from the Auth0 Management API once the management
  // token is available. Called as the onToken callback below.
  // user_metadata is never injected into the ID token by the Post Login Action,
  // so it must be read from the Management API directly on every page load.
  const fetchUserMetadataOnLoad = async (mgmtToken: string) => {
    try {
      const res = await fetch(
        `https://${process.env.NEXT_PUBLIC_AUTH0_DOMAIN}/api/v2/users/${user?.sub}`,
        { headers: { Authorization: `Bearer ${mgmtToken}` } }
      );
      if (!res.ok) {
        // Log internally for debugging but do not surface details to the UI —
        // Auth0 error bodies can contain scope names and token status information.
        const detail = await res.text();
        Sentry.captureMessage(
          `fetchUserMetadataOnLoad failed: ${res.status} ${detail}`,
          "error"
        );
        console.error("Failed to load user_metadata:", res.status, detail);
        return;
      }
      const data = await res.json();
      const metadata: Auth0UserMetadata = {
        ...EMPTY_USER_METADATA,
        ...data.user_metadata
      };
      // Write to ref first (deep-link case: userData may not be set yet).
      apiUserMetadataRef.current = metadata;
      // Update userData if it is already initialised (post-navigation case).
      setUserData(prev => (prev ? { ...prev, user_metadata: metadata } : prev));
    } catch (e) {
      console.error("Failed to load user_metadata:", e);
      Sentry.captureException(e);
    }
  };

  // Management token — used only for reading/writing user_metadata.
  //
  // ⚠️ The Auth0 Management API does NOT support "Allow Offline Access", so
  // getAccessTokenSilently always falls back to iframe silent auth. This fails
  // in private/incognito windows due to 3rd-party cookie restrictions.
  // useAccessToken catches errors gracefully: if the token cannot be obtained,
  // the ref stays undefined and updateUserMetadata becomes a no-op.
  // Skip that request in local dev (localhost) to avoid consent/login_required churn on localhost.
  const [auth0ManagementTokenRef] = useAccessToken(
    {
      shouldRefreshToken: Boolean(user?.sub) && !DEV,
      auth0Options: {
        audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE || ""
      },
      onToken: fetchUserMetadataOnLoad
    },
    [isAuthenticated]
  );

  // ─── User metadata save ───────────────────────────────────────────────────

  const updateUserMetadata = async (user_metadata: Auth0UserMetadata) => {
    if (!auth0ManagementTokenRef.current) return;
    try {
      const res = await fetch(
        `https://${process.env.NEXT_PUBLIC_AUTH0_DOMAIN}/api/v2/users/${user?.sub}`,
        {
          method: "PATCH",
          body: JSON.stringify({ user_metadata }),
          headers: {
            Authorization: `Bearer ${auth0ManagementTokenRef.current}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (!res.ok) {
        // Auth0 error bodies can contain scope names, token status, and other
        // implementation details — capture internally but never surface to UI.
        const detail = await res.text();
        Sentry.captureMessage(
          `updateUserMetadata failed: ${res.status} ${detail}`,
          "error"
        );
        throw new Error(
          "Auth0 Management API returned a non-2xx response while saving user profile metadata"
        );
      }
      const data = await res.json();
      setUserData(prev =>
        prev
          ? {
              ...prev,
              user_metadata: { ...EMPTY_USER_METADATA, ...data.user_metadata }
            }
          : prev
      );
      showToast({ title: "Profile saved", type: "success" });
    } catch (e) {
      // Show a generic message — raw error details could disclose API internals.
      Sentry.captureException(e);
      showToast({
        title: "Failed to save profile. Please try again.",
        type: "error"
      });
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        // @ts-ignore
        profile,
        hasRole,
        // @ts-ignore
        userData,
        updateUserMetadata,
        setProfile,
        authProvider,
        isLoading: isAuth0Loading,
        isTokenLoading: isAuth0BinderTokenLoading,
        token: binderTokenRef,
        refreshTokenCallback,
        // #DEPRECATED - use isAuthenticated instead once migration is complete of all users to Auth0
        isAuthenticated: Boolean(
          isAuthenticated ? binderTokenRef.current : profile?.sub
        ),
        // @ts-ignore
        loginWithSocial: () => {
          if (isMounted) {
            sessionStorage.setItem(AUTH_REQUESTED_KEY, "true");
          }
        },
        logout: () => {
          if (isMixpanelInitialisedRef.current) {
            mixpanel.reset();
            isMixpanelInitialisedRef.current = false;
          }
          setIsAuth0Loading(true); // stops additional redirects after logging out
          if (isMounted) {
            sessionStorage.removeItem(AUTH_TYPE_KEY);
          }
          return logout({
            returnTo: isMounted ? window.location.origin : ""
          });
        }
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export { ProfileProvider };
